/**
 * Modales "Revisar" de la Comisión (S21) para la página /admin/validaciones.
 *
 * Permiten que la Comisión corrija datos triviales de un validable PENDIENTE
 * antes de aprobarlo (ej. completar un nombre que el vecino dejó en iniciales,
 * arreglar un RUT). Reusan los editores existentes:
 *  - residente → PATCH /me/residentes/{id} (actualizarResidenteConPersona)
 *  - vehículo  → PATCH /vehiculos/{id} (actualizarVehiculo)
 * Ambos endpoints aceptan a la Comisión vía el escape hatch de staff
 * (chequear_permiso_o_403 / usuario_tiene_acceso_lectura_lote). Editar un
 * pendiente lo deja pendiente; luego se valida con el botón "Validar".
 *
 * La patente del vehículo es inmutable en edición (el backend la rechaza); si
 * está mal, corresponde rechazar, no revisar.
 */
import { useState, type FormEvent } from 'react';

import { ApiError } from '../../lib/api/client';
import {
  actualizarResidenteConPersona,
  type ParentescoLiteral,
} from '../../lib/api/residentes';
import { actualizarVehiculo } from '../../lib/api/vehiculos';
import type {
  ResidentePendiente,
  VehiculoPendiente,
} from '../../lib/api/validaciones';
import {
  COLORES_VEHICULO,
  type ColorVehiculo,
  type TipoTraccion,
} from '../../lib/api/types';
import { validarRutChileno } from '../../lib/format';
import { PARENTESCO_LABEL } from '../../lib/labels';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { InputCelular } from '../ui/InputCelular';
import { InputRut, type RutValidez } from '../ui/InputRut';

function detalleError(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.body === 'object' &&
      err.body !== null &&
      'detail' in err.body
      ? String((err.body as { detail: unknown }).detail)
      : `Error ${err.status}`;
  }
  return 'Error de red';
}

const INPUT_CLS =
  'w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2';

interface ModalRevisarResidenteProps {
  residente: ResidentePendiente;
  onCerrar: () => void;
  onListo: () => void;
}

export function ModalRevisarResidente({
  residente,
  onCerrar,
  onListo,
}: ModalRevisarResidenteProps) {
  const [nombre, setNombre] = useState(residente.persona_nombre_completo);
  const [rut, setRut] = useState(residente.persona_rut ?? '');
  const [rutValidez, setRutValidez] = useState<RutValidez>(() =>
    validarRutChileno(residente.persona_rut ?? ''),
  );
  const [email, setEmail] = useState(residente.persona_email ?? '');
  const [celular, setCelular] = useState(residente.persona_celular ?? '');
  const [parentesco, setParentesco] = useState<ParentescoLiteral>(
    residente.parentesco as ParentescoLiteral,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valido = nombre.trim().length > 0 && rutValidez.valid;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valido || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await actualizarResidenteConPersona(residente.id, {
        nombre_completo: nombre.trim(),
        rut: rutValidez.canonico,
        email: email.trim() || null,
        celular: celular.trim() || null,
        parentesco,
        desde: residente.desde,
      });
      onListo();
    } catch (err) {
      setError(detalleError(err));
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Revisar residente"
      onClose={onCerrar}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="form-revisar-residente"
            disabled={!valido || submitting}
          >
            {submitting ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }
    >
      <form
        id="form-revisar-residente"
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <p className="text-xs text-text-muted">
          Casa {residente.lote_identificador}. Corrige lo necesario; el
          residente queda en revisión para que luego lo valides.
        </p>
        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Nombre completo <span className="text-state-danger">*</span>
          </span>
          <input
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            maxLength={200}
            className={INPUT_CLS}
            required
          />
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              RUT <span className="text-state-danger">*</span>
            </span>
            <InputRut
              value={rut}
              onChange={(f, v) => {
                setRut(f);
                setRutValidez(v);
              }}
              aria-label="RUT del residente"
            />
          </label>
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">Parentesco</span>
            <select
              value={parentesco}
              onChange={(e) =>
                setParentesco(e.target.value as ParentescoLiteral)
              }
              className={INPUT_CLS}
            >
              {(Object.keys(PARENTESCO_LABEL) as ParentescoLiteral[]).map(
                (p) => (
                  <option key={p} value={p}>
                    {PARENTESCO_LABEL[p]}
                  </option>
                ),
              )}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Email <span className="text-text-muted">(opcional)</span>
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={255}
              className={INPUT_CLS}
            />
          </label>
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Celular <span className="text-text-muted">(opcional)</span>
            </span>
            <InputCelular value={celular} onChange={setCelular} />
          </label>
        </div>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

interface ModalRevisarVehiculoProps {
  vehiculo: VehiculoPendiente;
  onCerrar: () => void;
  onListo: () => void;
}

export function ModalRevisarVehiculo({
  vehiculo,
  onCerrar,
  onListo,
}: ModalRevisarVehiculoProps) {
  const [marca, setMarca] = useState(vehiculo.marca ?? '');
  const [modelo, setModelo] = useState(vehiculo.modelo ?? '');
  const [color, setColor] = useState<ColorVehiculo | ''>(
    (vehiculo.color as ColorVehiculo | null) ?? '',
  );
  const [colorOtro, setColorOtro] = useState('');
  // El tipo de tracción faltaba y por eso la Comisión no podía corregirlo
  // (reportado 2026-09-04 con la patente ZZZZ-99, marcada híbrido siendo a
  // gasolina). No es un dato cosmético: alimenta el catastro de Bomberos, que
  // ante una emergencia necesita saber qué vehículos tienen batería de litio.
  // Mismo par de casillas mutex que usa el vecino en `ModalVehiculo`: ambas
  // sin marcar = combustión.
  const [esHibrido, setEsHibrido] = useState(
    vehiculo.tipo_traccion === 'hibrido',
  );
  const [esElectrico, setEsElectrico] = useState(
    vehiculo.tipo_traccion === 'electrico',
  );
  const tipoTraccion: TipoTraccion = esElectrico
    ? 'electrico'
    : esHibrido
      ? 'hibrido'
      : 'combustion';
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const colorOtroVisible = color === 'Otro';
  const valido =
    marca.trim().length > 0 &&
    modelo.trim().length > 0 &&
    (!colorOtroVisible || colorOtro.trim().length > 0);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valido || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await actualizarVehiculo(vehiculo.id, {
        marca: marca.trim() || null,
        modelo: modelo.trim() || null,
        color: color || null,
        color_otro: color === 'Otro' ? colorOtro.trim() || null : null,
        tipo_traccion: tipoTraccion,
      });
      onListo();
    } catch (err) {
      setError(detalleError(err));
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Revisar vehículo"
      onClose={onCerrar}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="form-revisar-vehiculo"
            disabled={!valido || submitting}
          >
            {submitting ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }
    >
      <form
        id="form-revisar-vehiculo"
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <p className="text-xs text-text-muted">
          Casa {vehiculo.lote_identificador} · patente{' '}
          <span className="font-mono text-text-secondary">
            {vehiculo.patente}
          </span>{' '}
          (la patente no se edita; si está mal, rechaza). Corrige lo necesario;
          el vehículo queda en revisión para que luego lo valides.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Marca <span className="text-state-danger">*</span>
            </span>
            <input
              type="text"
              value={marca}
              onChange={(e) => setMarca(e.target.value)}
              maxLength={60}
              className={INPUT_CLS}
              required
            />
          </label>
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Modelo <span className="text-state-danger">*</span>
            </span>
            <input
              type="text"
              value={modelo}
              onChange={(e) => setModelo(e.target.value)}
              maxLength={60}
              className={INPUT_CLS}
              required
            />
          </label>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Color</span>
          <select
            value={color}
            onChange={(e) => setColor(e.target.value as ColorVehiculo | '')}
            className={INPUT_CLS}
          >
            <option value="">— Sin especificar —</option>
            {COLORES_VEHICULO.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>

        {colorOtroVisible && (
          <label className="text-sm space-y-1 block">
            <span className="text-text-secondary">
              Especifica el color <span className="text-state-danger">*</span>
            </span>
            <input
              type="text"
              value={colorOtro}
              onChange={(e) => setColorOtro(e.target.value)}
              maxLength={40}
              className={INPUT_CLS}
              required
            />
          </label>
        )}

        <fieldset className="space-y-2 border border-white/10 rounded-sm p-3">
          <legend className="text-sm text-text-secondary px-1">
            ¿Vehículo con batería de litio?
          </legend>
          <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
            <input
              type="checkbox"
              checked={esHibrido}
              onChange={(e) => {
                setEsHibrido(e.target.checked);
                if (e.target.checked) setEsElectrico(false);
              }}
              className="rounded-sm accent-orange"
            />
            <span>Híbrido</span>
          </label>
          <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
            <input
              type="checkbox"
              checked={esElectrico}
              onChange={(e) => {
                setEsElectrico(e.target.checked);
                if (e.target.checked) setEsHibrido(false);
              }}
              className="rounded-sm accent-orange"
            />
            <span>Eléctrico</span>
          </label>
          <p className="text-xs text-text-muted">
            Si no marcas ninguno, el vehículo es a bencina o diésel. Este dato
            va al catastro de Bomberos: ante una emergencia necesitan saber qué
            vehículos tienen batería.
          </p>
        </fieldset>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
